import React, { useCallback, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Text,
  Flex,
  Button,
  Select,
  Icon,
  Popover,
  Badge,
} from "orcs-design-system";
import styled from "styled-components";
import {
  LayersMetric,
  SpanMetric,
  ManagerToICRatioMetric,
  ManagementOverheadMetric,
  MemberCountMetric,
  useTeamMetrics,
} from "src/components/shared/TeamMetrics";
import { useOrgChartContext } from "src/components/OrgChart/context/OrgChartContext";
import PATHS, { TEAM_TAB, url } from "src/consts/urlPaths";
import {
  TeamInsightsScenarioProvider,
  useTeamInsightsScenario,
} from "src/contexts/teamInsights/TeamInsightsScenarioContext";
import { useTeamInsightsFeatureEnabled } from "src/components/OrgChart/odDemo/featureFlags";
import { useWorkspace } from "src/contexts/global/WorkspaceContext";
import { getGroupBadgeVariant } from "src/util/group";
import { getGroupType } from "src/util/groupType";

const SCENARIO_OPTIONS = [
  { value: "conservative", label: "Conservative" },
  { value: "standard", label: "Standard" },
  { value: "aggressive", label: "Aggressive" },
];

const TeamBadge = styled(Badge)`
  display: inline-block;
`;

const MetricsPanelContent = () => {
  const { scenario, setScenario } = useTeamInsightsScenario();
  const { metrics, orgMetrics, loading, hasTeam, currentTeam } =
    useTeamMetrics(scenario);
  const { state } = useOrgChartContext();
  const workspace = useWorkspace();
  const groupTypes = workspace?.config?.groupTypes;
  const history = useHistory();
  const enableDemoODOptions = useTeamInsightsFeatureEnabled();
  const previousScenarioRef = useRef(scenario);
  const previousTargetsRef = useRef(null);
  const targetsSnapshotRef = useRef(null);

  // Determine which targets changed - compare current targets with snapshot
  const targetChanged = useMemo(() => {
    // On first render or when scenario changes, capture current targets as snapshot
    if (
      previousTargetsRef.current === null ||
      previousScenarioRef.current !== scenario
    ) {
      // Store current targets as snapshot for comparison
      if (previousTargetsRef.current !== null) {
        // Scenario changed - store old targets before updating
        targetsSnapshotRef.current = { ...previousTargetsRef.current };
      }
      // Update refs with current targets
      previousTargetsRef.current = {
        layers: metrics.layers.target,
        span: metrics.span.target,
        managerToICRatio: metrics.managerToICRatio.target,
        managementOverhead: metrics.managementOverhead.target,
        headcountReduction: metrics.headcountReduction.target,
      };
      previousScenarioRef.current = scenario;

      // If we have a snapshot, compare; otherwise no changes on first render
      if (targetsSnapshotRef.current === null) {
        return {
          layers: false,
          span: false,
          managerToICRatio: false,
          managementOverhead: false,
          headcountReduction: false,
        };
      }
    }

    // Compare current targets with snapshot
    const prev = targetsSnapshotRef.current || previousTargetsRef.current;
    if (!prev) {
      return {
        layers: false,
        span: false,
        managerToICRatio: false,
        managementOverhead: false,
        headcountReduction: false,
      };
    }

    // Compare targets (handle both numbers and objects for span)
    const compareTarget = (current, previous) => {
      if (current === undefined || current === null) {
        return false;
      }
      if (previous === undefined || previous === null) {
        return false;
      }
      if (typeof current === "object" && typeof previous === "object") {
        // For span which has {min, max}
        return current.min !== previous.min || current.max !== previous.max;
      }
      return current !== previous;
    };

    const changed = {
      layers: compareTarget(metrics.layers.target, prev.layers),
      span: compareTarget(metrics.span.target, prev.span),
      managerToICRatio: compareTarget(
        metrics.managerToICRatio.target,
        prev.managerToICRatio
      ),
      managementOverhead: compareTarget(
        metrics.managementOverhead.target,
        prev.managementOverhead
      ),
      headcountReduction: compareTarget(
        metrics.headcountReduction.target,
        prev.headcountReduction
      ),
    };

    // Update snapshot after comparison for next render (only if scenario hasn't changed)
    if (previousScenarioRef.current === scenario) {
      targetsSnapshotRef.current = {
        layers: metrics.layers.target,
        span: metrics.span.target,
        managerToICRatio: metrics.managerToICRatio.target,
        managementOverhead: metrics.managementOverhead.target,
        headcountReduction: metrics.headcountReduction.target,
      };
    }

    return changed;
  }, [scenario, metrics]);

  const handleViewInsights = useCallback(() => {
    const teamId = state?.currentViewTeam?.id;
    if (teamId) {
      const path = url(PATHS.TEAM_DETAILS, {
        id: teamId,
        tabType: TEAM_TAB.INSIGHTS,
      });
      history.push(path);
    }
  }, [state?.currentViewTeam?.id, history]);

  // Get the current selected option for Select component
  const selectedScenarioOption = useMemo(() => {
    return (
      SCENARIO_OPTIONS.find((opt) => opt.value === scenario) ||
      SCENARIO_OPTIONS[1]
    ); // Default to "standard"
  }, [scenario]);

  // Get team badge variant for display
  const teamBadgeVariant = useMemo(() => {
    if (!currentTeam || !groupTypes) {
      return null;
    }
    return getGroupBadgeVariant(currentTeam, groupTypes);
  }, [currentTeam, groupTypes]);

  // Get team type label (Demand or Supply)
  const teamTypeLabel = useMemo(() => {
    if (!currentTeam || !currentTeam.type || !groupTypes) {
      return null;
    }
    const groupType = getGroupType(currentTeam, groupTypes);
    if (!groupType) {
      return null;
    }
    const allocationConfig = workspace?.config?.allocation || {};
    const { demandLabel = "Demand", supplyLabel = "Supply" } = allocationConfig;
    if (groupType.isSupply) {
      return supplyLabel;
    }
    if (groupType.isDemand) {
      return demandLabel;
    }
    return null;
  }, [currentTeam, groupTypes, workspace?.config?.allocation]);

  if (!hasTeam) {
    return (
      <Box p="r">
        <Text>Select a team to view insights</Text>
      </Box>
    );
  }

  return (
    <Box p="r">
      <Flex flexDirection="column" gap="xs">
        {/* Team Name and Badge Display */}
        {currentTeam && (
          <Box mb="xs">
            <Flex alignItems="center" gap="xs" flexWrap="wrap">
              {teamTypeLabel && (
                <Text fontSize="s" color="textSecondary">
                  {teamTypeLabel}:
                </Text>
              )}
              {teamBadgeVariant && (
                <TeamBadge variant={teamBadgeVariant} display="inline-block">
                  {currentTeam.name || currentTeam.id}
                </TeamBadge>
              )}
              {!teamBadgeVariant && (
                <Text fontSize="s" fontWeight="semibold">
                  {currentTeam.name || currentTeam.id}
                </Text>
              )}
            </Flex>
          </Box>
        )}
        {/* Scenario Selector with Help Icon */}
        <Flex
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap="s"
          mb="s"
        >
          <Flex alignItems="center" gap="xs">
            <Text fontSize="s" fontWeight="semibold">
              Target Scenario
            </Text>
            <Popover
              direction="bottom"
              text="Format: Current / Target / Delta. Current is the team's actual value. Target is the recommended value based on the selected scenario. Delta shows the difference (positive = above target, negative = below target)."
            >
              <Box
                as="button"
                type="button"
                style={{
                  background: "none",
                  border: "none",
                  cursor: "help",
                  padding: 0,
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                <Icon
                  icon={["far", "question-circle"]}
                  fontSize="s"
                  color="greyDark"
                />
              </Box>
            </Popover>
          </Flex>
          <Box minWidth="180px">
            <Select
              height="30px"
              data-testid="orgchart-scenario-select"
              options={SCENARIO_OPTIONS}
              value={selectedScenarioOption}
              onChange={(option) => {
                if (option && option.value) {
                  setScenario(option.value);
                }
              }}
            />
          </Box>
        </Flex>
        <LayersMetric
          metric={metrics.layers}
          loading={loading}
          targetChanged={targetChanged.layers}
        />
        <SpanMetric
          metric={metrics.span}
          orgMetric={orgMetrics?.span}
          loading={loading}
          targetChanged={targetChanged.span}
        />
        <ManagerToICRatioMetric
          metric={metrics.managerToICRatio}
          orgMetric={orgMetrics?.managerToICRatio}
          loading={loading}
          targetChanged={targetChanged.managerToICRatio}
        />
        <ManagementOverheadMetric
          metric={metrics.managementOverhead}
          orgMetric={orgMetrics?.managementOverhead}
          loading={loading}
          targetChanged={targetChanged.managementOverhead}
        />
        <MemberCountMetric
          metric={metrics.headcountReduction}
          orgMetric={orgMetrics}
          currentTeam={currentTeam}
          loading={loading}
          targetChanged={targetChanged.headcountReduction}
        />
      </Flex>
      {enableDemoODOptions && (
        <Box mt="r">
          <Button
            type="button"
            variant="secondary"
            size="small"
            onClick={handleViewInsights}
          >
            Explore Insights
          </Button>
        </Box>
      )}
    </Box>
  );
};

const MetricsPanel = () => {
  return (
    <TeamInsightsScenarioProvider>
      <MetricsPanelContent />
    </TeamInsightsScenarioProvider>
  );
};

export default MetricsPanel;
